// Применение брендинг-темы заведения к документу в рантайме:
// - CSS-переменные (--brand-600, --app-font) → Tailwind подхватит;
// - <title>;
// - favicon (<link rel="icon">);
// - apple-touch-icon (iOS «На главный экран»);
// - <link rel="manifest"> (для Android «Установить приложение»);
// - <meta name="theme-color">.
//
// Производные оттенки --brand-50..900 заданы в index.css через color-mix,
// поэтому достаточно подменить только --brand-600.

import { asAbsoluteUrl } from './url';

export type Theme = {
  primary?: string | null;
  fontFamily?: string | null;
  faviconUrl?: string | null;
  iconUrl?: string | null;
};

export type ThemeContext = {
  establishmentName?: string | null;
  host?: string | null;
};

export function applyTheme(theme: Theme | null | undefined, ctx: ThemeContext = {}) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;

  // В dev помогает понять «почему цвет не сменился» — смотри в консоли браузера.
  if (import.meta.env?.DEV) {
    // eslint-disable-next-line no-console
    console.debug('[theme] apply', { theme, ctx });
  }

  if (theme?.primary) {
    root.style.setProperty('--brand-600', theme.primary);
  } else {
    root.style.removeProperty('--brand-600');
  }

  if (theme?.fontFamily) {
    root.style.setProperty('--app-font', theme.fontFamily);
  } else {
    root.style.removeProperty('--app-font');
  }

  // Защита на случай, когда inline-style по каким-то причинам не перекрывает
  // :root из index.css (кэширование, PWA SW, сторонние стили). Жёстко инжектим
  // <style id="resstro-theme-override"> с !important — это гарантированно применится.
  injectOverride({ primary: theme?.primary || null, fontFamily: theme?.fontFamily || null });

  if (import.meta.env?.DEV && theme?.primary) {
    // Читаем итоговое вычисленное значение переменной — видно сразу, применилось или нет.
    setTimeout(() => {
      const computed = getComputedStyle(document.documentElement).getPropertyValue('--brand-600').trim();
      // eslint-disable-next-line no-console
      console.debug('[theme] computed --brand-600 =', computed, 'expected =', theme.primary);
    }, 0);
  }

  // Title
  if (ctx.establishmentName) {
    document.title = `${ctx.establishmentName} — Меню`;
  }

  // Theme color (для адресной строки мобильного Safari/Chrome)
  setMeta('theme-color', theme?.primary || '#4f62e0');

  // Favicon
  const favicon = asAbsoluteUrl(theme?.faviconUrl ?? null);
  setLink('icon', favicon || '/icon.svg', favicon?.endsWith('.svg') ? 'image/svg+xml' : undefined);

  // Apple touch icon (iOS Home Screen)
  const appleIcon = asAbsoluteUrl(theme?.iconUrl ?? theme?.faviconUrl ?? null);
  setLink('apple-touch-icon', appleIcon || '/icon.svg');

  // Manifest (Android Add to Home Screen)
  if (ctx.host) {
    setLink('manifest', `/api/client/manifest?host=${encodeURIComponent(ctx.host)}`);
  } else {
    setLink('manifest', '/api/client/manifest');
  }
}

function setLink(rel: string, href: string, type?: string) {
  if (!href) return;
  let el = document.head.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
  if (type) el.type = type;
}

function injectOverride({ primary, fontFamily }: { primary: string | null; fontFamily: string | null }) {
  const id = 'resstro-theme-override';
  let el = document.getElementById(id) as HTMLStyleElement | null;
  if (!el) {
    el = document.createElement('style');
    el.id = id;
    document.head.appendChild(el);
  }
  const rules: string[] = [];
  if (primary) rules.push(`--brand-600: ${primary} !important;`);
  if (fontFamily) rules.push(`--app-font: ${fontFamily} !important;`);
  el.textContent = rules.length ? `:root{${rules.join('')}}` : '';
}

function setMeta(name: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.name = name;
    document.head.appendChild(el);
  }
  el.content = content;
}
